import moment from 'moment';
import { useMemo } from 'react';
import { twMerge } from 'tailwind-merge';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import {
  BuyerStatementInvoice,
  BuyerStatementCreditMemo,
  QuickbooksInvoice,
  QuickbooksCreditMemo,
  formatMoney,
  isCreditMemoSelectable,
} from '@dill/dill-shared';
import { DillCheckbox } from '../../../general/Dill';
import DillTable, { DillColumn } from '../../../general/Dill/DillTable';

const getDocumentType = (liveInv?: QuickbooksInvoice, liveCm?: QuickbooksCreditMemo): string => {
  if (liveInv?.recordType === 'FINANCE_CHARGE') return 'Finance Charge';
  if (liveInv?.recordType === 'DEBIT_MEMO') return 'Debit Memo';
  if (liveInv) return 'Invoice';
  if (liveCm?.recordType === 'PAYMENT') return 'Payment';
  if (liveCm?.recordType === 'MISC_CREDITS') return 'Misc Credits';
  if (liveCm?.recordType === 'CREDIT_INVOICE') return 'Credits Invoice';
  if (liveCm?.recordType === 'CREDIT_MEMO') return 'Credit Memo';
  if (liveCm) return 'Credit';
  return '\u2014';
};

type StatementRow =
  | { kind: 'invoice'; invoice: BuyerStatementInvoice; liveInvoice?: QuickbooksInvoice }
  | {
      kind: 'credit_memo';
      creditMemo: BuyerStatementCreditMemo;
      liveCreditMemo?: QuickbooksCreditMemo;
    };

interface StatementExpandableTableProps {
  invoices: BuyerStatementInvoice[];
  creditMemos: BuyerStatementCreditMemo[];
  liveInvoices?: QuickbooksInvoice[];
  liveCreditMemos?: QuickbooksCreditMemo[];
  selectedIds: Set<string>;
  onSelectionChange: (selectedIds: Set<string>) => void;
  integrationType?: string | null;
  pendingCreditMemoIds?: string[];
  totalPendingTransactionsAmount: number;
  totalCurrentBalance: number;
}

const StatementExpandableTable = ({
  invoices,
  creditMemos,
  liveInvoices,
  liveCreditMemos,
  selectedIds,
  onSelectionChange,
  integrationType,
  pendingCreditMemoIds,
  totalPendingTransactionsAmount,
  totalCurrentBalance,
}: StatementExpandableTableProps) => {
  const toggleId = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    onSelectionChange(next);
  };

  const rows: StatementRow[] = useMemo(() => {
    const invoiceRows: StatementRow[] = invoices.map((inv) => ({
      kind: 'invoice' as const,
      invoice: inv,
      liveInvoice: liveInvoices?.find((li) => li.id === inv.id),
    }));
    const creditRows: StatementRow[] = creditMemos.map((cm) => ({
      kind: 'credit_memo' as const,
      creditMemo: cm,
      liveCreditMemo: liveCreditMemos?.find((lcm) => lcm.id === cm.id),
    }));
    return [...invoiceRows, ...creditRows];
  }, [invoices, creditMemos, liveInvoices, liveCreditMemos]);

  const selectableIds = useMemo(() => {
    const ids: string[] = [];
    invoices.forEach((inv) => {
      const liveInv = liveInvoices?.find((li) => li.id === inv.id);
      const isPaid = liveInv && liveInv.balance === 0;
      const isTransactionPending = liveInv?.dillPaymentStatus === 'TRANSACTION_PENDING';
      const isDeleted = liveInv?.isDeleted === true;
      if (!isPaid && !isTransactionPending && !isDeleted) ids.push(inv.id);
    });
    creditMemos.forEach((cm) => {
      const liveCm = liveCreditMemos?.find((lcm) => lcm.id === cm.id);
      const isPending = pendingCreditMemoIds?.includes(cm.id);
      const isUsed = !liveCm;
      const isTransactionPending = liveCm?.dillPaymentStatus === 'TRANSACTION_PENDING';
      if (
        !isPending &&
        !isUsed &&
        !isTransactionPending &&
        isCreditMemoSelectable(liveCm?.recordType, integrationType)
      ) {
        ids.push(cm.id);
      }
    });
    return ids;
  }, [invoices, creditMemos, liveInvoices, liveCreditMemos, integrationType, pendingCreditMemoIds]);

  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selectedIds.has(id));

  const toggleAll = () => {
    if (allSelected) {
      onSelectionChange(new Set());
    } else {
      onSelectionChange(new Set(selectableIds));
    }
  };

  const totalInvoiceBalance = invoices.reduce((sum, inv) => sum + (inv.balance ?? 0), 0);
  const totalCreditRemaining = creditMemos.reduce((sum, cm) => sum + (cm.remainingCredit ?? 0), 0);
  const netTotal = totalInvoiceBalance - totalCreditRemaining;

  const NET_TERMS_REGEX = /^(\d+%\s?.*|.*\s?\d+%)$/;
  const CVS_PLUMBING_DISCOUNT_TERMS = ['210N25', '210N30', '210N30X15'];
  const DISCOUNT_INTEGRATIONS = [
    'MANUAL_UPLOAD',
    'DDI_MANUAL_UPLOAD',
    'P21_MANUAL_UPLOAD',
    'SAGE_100',
    'DISTRIBUTION_ONE',
    'RUTTER_QBD',
    'ECLIPSE',
    'ECLIPSE_MANUAL_UPLOAD',
    'EAGLE_MANUAL_UPLOAD',
  ];

  const columns: DillColumn<StatementRow>[] = useMemo(() => {
    const cols: DillColumn<StatementRow>[] = [
      {
        key: '_select',
        label: <DillCheckbox isChecked={allSelected} onChange={toggleAll} />,
        width: 44,
        sortable: false,
        render: (_, row) => {
          const id = row.kind === 'invoice' ? row.invoice.id : row.creditMemo.id;
          let selectable = true;
          if (row.kind === 'invoice') {
            const isPaid = row.liveInvoice && row.liveInvoice.balance === 0;
            const isTransactionPending =
              row.liveInvoice?.dillPaymentStatus === 'TRANSACTION_PENDING';
            const isDeleted = row.liveInvoice?.isDeleted === true;
            selectable = !isPaid && !isTransactionPending && !isDeleted;
          } else {
            const isPending = pendingCreditMemoIds?.includes(row.creditMemo.id);
            const isUsed = !row.liveCreditMemo;
            const isTransactionPending =
              row.liveCreditMemo?.dillPaymentStatus === 'TRANSACTION_PENDING';
            selectable =
              !isPending &&
              !isUsed &&
              !isTransactionPending &&
              isCreditMemoSelectable(
                (row as { kind: 'credit_memo'; liveCreditMemo?: QuickbooksCreditMemo })
                  .liveCreditMemo?.recordType,
                integrationType
              );
          }
          return (
            <DillCheckbox
              isChecked={selectedIds.has(id)}
              onChange={() => toggleId(id)}
              disabled={!selectable}
            />
          );
        },
      },
      {
        key: 'docNumber',
        label: 'Doc #',
        width: 120,
        filterable: true,
        getSortValue: (row) =>
          row.kind === 'invoice'
            ? row.invoice.docNumber || row.invoice.id
            : row.creditMemo.docNumber || row.creditMemo.id,
        getSearchValue: (row) =>
          row.kind === 'invoice'
            ? row.invoice.docNumber || row.invoice.id
            : row.creditMemo.docNumber || row.creditMemo.id,
        render: (_, row) => {
          const docNum =
            row.kind === 'invoice'
              ? row.invoice.docNumber || row.invoice.id
              : row.creditMemo.docNumber || row.creditMemo.id;
          const cls =
            row.kind === 'invoice' ? 'text-gray-700 font-medium' : 'text-gray-500 font-medium';
          const isDeleted = row.kind === 'invoice' && row.liveInvoice?.isDeleted === true;
          return (
            <div className="flex flex-col">
              <span className={cls}>{docNum}</span>
              {isDeleted && (
                <span className="text-[9px] text-red-500 font-semibold uppercase tracking-wide">
                  Deleted
                </span>
              )}
            </div>
          );
        },
      },
      {
        key: 'type',
        label: 'Type',
        width: 130,
        cellClassName: 'text-gray-500 text-xs',
        filterable: true,
        getSortValue: (row) =>
          row.kind === 'invoice'
            ? getDocumentType(row.liveInvoice, undefined)
            : getDocumentType(undefined, row.liveCreditMemo),
        render: (_, row) =>
          row.kind === 'invoice'
            ? getDocumentType(row.liveInvoice, undefined)
            : getDocumentType(undefined, row.liveCreditMemo),
      },
      {
        key: 'job',
        label: 'Job',
        width: 160,
        cellClassName: 'text-gray-500 text-xs',
        filterable: true,
        getSortValue: (row) =>
          row.kind === 'invoice'
            ? row.liveInvoice?.quickBooksJob?.jobName ?? ''
            : row.liveCreditMemo?.quickBooksJob?.jobName ?? '',
        render: (_, row) => {
          const job =
            row.kind === 'invoice'
              ? row.liveInvoice?.quickBooksJob
              : row.liveCreditMemo?.quickBooksJob;
          const jobName = job?.jobName;
          const jobNumber = job?.jobNumber;
          if (!jobName && !jobNumber) return '\u2014';
          const jobLabel =
            jobNumber && jobName && jobNumber !== jobName
              ? `${jobNumber} - ${jobName}`
              : jobName || jobNumber;
          const jobAddress = `${job?.address || ''} ${job?.city || ''} ${job?.state || ''}`.trim();
          const rowId = row.kind === 'invoice' ? row.invoice.id : row.creditMemo.id;
          return (
            <div className="w-full px-0.5">
              <p className="font-bold text-xs text-gray-700 w-full truncate">{jobLabel}</p>
              {jobAddress && (
                <>
                  <p
                    data-tooltip-id={'statement-job-address-' + rowId}
                    className="text-2xs w-full line-clamp-2 cursor-default">
                    {jobAddress}
                  </p>
                  <ReactTooltip
                    id={'statement-job-address-' + rowId}
                    place="top"
                    variant="dark"
                    opacity="100%"
                    style={{ background: '#222', width: '240px', zIndex: 99999, fontSize: '9px' }}>
                    <span>{jobAddress}</span>
                  </ReactTooltip>
                </>
              )}
            </div>
          );
        },
      },
      {
        key: 'date',
        label: 'Date',
        width: 110,
        filterType: 'dateRange',
        filterable: true,
        cellClassName: 'text-gray-500 tabular-nums',
        getSortValue: (row) => {
          const ts = row.kind === 'invoice' ? row.invoice.createdAt : row.creditMemo.createdAt;
          return ts ? new Date(ts).getTime() : 0;
        },
        render: (_, row) => {
          if (row.kind === 'invoice') {
            const inv = row.invoice;
            const liveInv = row.liveInvoice;
            return inv.createdAt
              ? moment(inv.createdAt).utc().format('MM/DD/YYYY')
              : liveInv?.createdAt
              ? moment(liveInv.createdAt).utc().format('MM/DD/YYYY')
              : '\u2014';
          }
          const cm = row.creditMemo;
          const liveCm = row.liveCreditMemo;
          return cm.createdAt
            ? moment(cm.createdAt).utc().format('MM/DD/YYYY')
            : liveCm?.createdAt
            ? moment(liveCm.createdAt).utc().format('MM/DD/YYYY')
            : '\u2014';
        },
      },
      {
        key: 'dueDate',
        label: 'Due Date',
        filterType: 'dateRange',
        filterable: true,
        width: 120,
        cellClassName: 'text-gray-500 tabular-nums',
        getSortValue: (row) => {
          const ts = row.kind === 'invoice' ? row.invoice.dueDate : row.creditMemo.dueDate;
          return ts ? new Date(ts).getTime() : 0;
        },
        render: (_, row) => {
          if (row.kind === 'invoice') {
            const inv = row.invoice;
            const liveInv = row.liveInvoice;
            return inv.dueDate
              ? moment(inv.dueDate).utc().format('MM/DD/YYYY')
              : liveInv?.dueDate
              ? moment(liveInv.dueDate).utc().format('MM/DD/YYYY')
              : '\u2014';
          }
          const cm = row.creditMemo;
          const liveCm = row.liveCreditMemo;
          return cm.dueDate
            ? moment(cm.dueDate).utc().format('MM/DD/YYYY')
            : liveCm?.dueDate
            ? moment(liveCm.dueDate).utc().format('MM/DD/YYYY')
            : '\u2014';
        },
      },
      {
        key: 'terms',
        label: 'Terms',
        width: 110,
        cellClassName: 'text-gray-500',
        filterable: true,
        getSortValue: (row) =>
          row.kind === 'invoice'
            ? row.liveInvoice?.terms || ''
            : row.liveCreditMemo?.recordType || '',
        render: (_, row) => {
          if (row.kind === 'invoice') return row.liveInvoice?.terms || '\u2014';
          return '';
        },
      },
      {
        key: 'amount',
        label: 'Amount',
        width: 130,
        numeric: true,
        filterable: true,

        getSortValue: (row) =>
          row.kind === 'invoice' ? row.invoice.amount ?? 0 : -(row.creditMemo.totalAmount ?? 0),
        render: (_, row) => {
          if (row.kind === 'invoice') {
            const isCvsPlumbingDiscountTerm =
              integrationType === 'ECLIPSE_MANUAL_UPLOAD' &&
              CVS_PLUMBING_DISCOUNT_TERMS.includes(row.liveInvoice?.terms ?? '');
            const discountAvailable =
              (NET_TERMS_REGEX.test(row.liveInvoice?.terms ?? '') &&
                DISCOUNT_INTEGRATIONS.includes(integrationType ?? '')) ||
              isCvsPlumbingDiscountTerm;
            return (
              <div className="flex items-center justify-end  flex-col w-full">
                <span className="text-gray-600 text-xs w-full text-right">
                  {formatMoney(row.invoice.amount ?? 0, 2)}
                </span>
                {discountAvailable && (
                  <>
                    <p
                      data-tooltip-id={'possible-discount-' + row.invoice.id}
                      className="text-[9px] text-right PRIMARY_500-CLR w-full cursor-default">
                      Possible Discount
                    </p>
                    <ReactTooltip
                      id={'possible-discount-' + row.invoice.id}
                      place="top"
                      variant="dark"
                      opacity="100%"
                      style={{ background: '#222', width: '240px', zIndex: 99999 }}>
                      <span>
                        Click Pay, and any discounts will get automatically applied for you when you
                        select your payment method.
                      </span>
                    </ReactTooltip>
                  </>
                )}
              </div>
            );
          }
          return (
            <span className="text-gray-500">
              -{formatMoney(row.creditMemo.totalAmount ?? 0, 2)}
            </span>
          );
        },
        footer: formatMoney(totalInvoiceBalance - totalCreditRemaining, 2),
      },
      {
        key: 'stmtBalance',
        label: 'Stmt Balance',
        width: 150,
        numeric: true,
        filterable: true,
        getSortValue: (row) =>
          row.kind === 'invoice'
            ? row.invoice.balance ?? 0
            : -(row.creditMemo.remainingCredit ?? 0),
        render: (_, row) => {
          if (row.kind === 'invoice') {
            return (
              <span className="text-gray-900 font-medium">
                {formatMoney(row.invoice.balance ?? 0, 2)}
              </span>
            );
          }
          return (
            <span className="text-gray-500">
              -{formatMoney(row.creditMemo.remainingCredit ?? 0, 2)}
            </span>
          );
        },
        footer: formatMoney(netTotal, 2),
      },
    ];

    if (liveInvoices) {
      cols.push({
        key: 'currentBalance',
        label: 'Current Balance',
        width: 180,
        numeric: true,
        filterable: true,
        getSortValue: (row) => {
          if (row.kind === 'invoice') return row.liveInvoice?.balance ?? 0;
          return -(row.liveCreditMemo?.remainingCredit ?? 0);
        },
        render: (_, row) => {
          if (row.kind === 'invoice') {
            let currentBal = row.liveInvoice?.balance ?? null;
            const amountPaid =
              (row?.liveInvoice?.dillInvoicePaymentSummary?.amountPaid ?? 0) +
              (row?.liveInvoice?.dillInvoicePaymentSummary?.discountAmount ?? 0);
            if (row?.liveInvoice?.dillInvoicePaymentSummary && currentBal !== null) {
              currentBal = currentBal - amountPaid;
            }
            if (currentBal !== null) {
              return <span className="text-gray-700">{formatMoney(currentBal, 2)}</span>;
            }
            return '\u2014';
          }
          let currentCredit = row.liveCreditMemo?.balance ?? 0;
          const isPending = pendingCreditMemoIds?.includes(row.creditMemo.id);
          if (isPending) {
            currentCredit = 0;
          }
          return (
            <span className="text-gray-500">
              {currentCredit === 0 ? '' : '-'}
              {formatMoney(currentCredit, 2)}
            </span>
          );
        },
        footer: totalCurrentBalance ? formatMoney(totalCurrentBalance, 2) : undefined,
      });
    }

    cols.push({
      key: 'transactionStatus',
      label: 'Transaction Status',
      width: 180,
      filterable: true,
      getSortValue: (row) => {
        if (row.kind === 'invoice') return row.liveInvoice?.dillPaymentStatus || '';
        if (!row.liveCreditMemo) return '\u2014';
        const cmStatus =
          row.liveCreditMemo?.dillPaymentStatus ||
          (pendingCreditMemoIds?.includes(row.creditMemo.id) ? 'TRANSACTION_PENDING' : '\u2014');
        return cmStatus;
      },
      render: (_, row) => {
        let payment: number | null = null;
        let status: 'TRANSACTION_PENDING' | 'RECONCILIATION_PENDING' | null | undefined = undefined;
        if (row.kind === 'invoice') {
          status = row.liveInvoice?.dillPaymentStatus;
          if (status === 'TRANSACTION_PENDING') {
            const amountPaid =
              (row?.liveInvoice?.dillInvoicePaymentSummary?.amountPaid ?? 0) +
              (row?.liveInvoice?.dillInvoicePaymentSummary?.discountAmount ?? 0);
            if (row?.liveInvoice?.dillInvoicePaymentSummary) {
              payment = amountPaid;
            }
          }
        } else if (row.kind === 'credit_memo') {
          status =
            row.liveCreditMemo?.dillPaymentStatus ||
            (pendingCreditMemoIds?.includes(row.creditMemo.id) ? 'TRANSACTION_PENDING' : null);
          if (status === 'TRANSACTION_PENDING') {
            const isPending = pendingCreditMemoIds?.includes(row.creditMemo.id);
            if (isPending) {
              payment = row?.liveCreditMemo?.balance || null;
            }
          }
        }

        if (status === 'TRANSACTION_PENDING') {
          return (
            <div className="flex items-center justify-end gap-1.5 w-full">
              {payment !== null && row.kind === 'invoice' && (
                <p className="text-xs font-semibold mb-0">{formatMoney(payment, 2)}</p>
              )}
              {payment !== null && row.kind !== 'invoice' && (
                <p className="text-xs font-semibold mb-0">
                  {payment === 0 ? '' : '-'}
                  {formatMoney(payment, 2)}
                </p>
              )}
              <div
                className={twMerge(
                  'flex items-center px-1.5 py-0.5 rounded-xl justify-center',
                  status === 'TRANSACTION_PENDING'
                    ? 'bg-blue-100 text-blue-600'
                    : 'bg-gray-100 text-gray-500'
                )}>
                <p className="text-[10px] font-bold capitalize mb-0">
                  {status === 'TRANSACTION_PENDING' ? 'Pending' : '\u2014'}
                </p>
              </div>
            </div>
          );
        } else {
          return '\u2014';
        }
      },
      footer: (
        <div className="flex items-center justify-end gap-1.5 w-full">
          {totalPendingTransactionsAmount
            ? formatMoney(totalPendingTransactionsAmount, 2)
            : undefined}
          <div
            className={twMerge(
              'flex items-center px-1.5 py-0.5 rounded-xl justify-center bg-blue-100 text-blue-600 opacity-0'
            )}>
            <p className="text-[10px] font-bold capitalize mb-0">{'Pending'}</p>
          </div>
        </div>
      ),
    });

    return cols;
  }, [
    allSelected,
    selectedIds,
    integrationType,
    liveInvoices,
    totalInvoiceBalance,
    totalCreditRemaining,
    netTotal,
    totalCurrentBalance,
    pendingCreditMemoIds,
  ]);

  return (
    <div className="px-6 pb-4 pt-1">
      <DillTable<StatementRow>
        columns={columns}
        data={rows}
        rowKey={(row) => (row.kind === 'invoice' ? row.invoice.id : row.creditMemo.id)}
        sortable
        search={false}
        columnToggle={false}
        showCount={false}
        striped={false}
        showFooter
        stickyFooter={false}
        pagination={false}
        countLabel="documents"
      />
    </div>
  );
};

export default StatementExpandableTable;
